<template>
  <v-dialog v-model="mainModalIsOpen" :width="isMobileSized ? '' : '400px'" height="500px">
    <v-card class="panel pa-6" :class="{ regularPanel: !isMobileSized }">
      <div class="text-h4">
        Log in
      </div>
      <div class="mt-2">Don't have an Adacado account yet? </div>
      <div class="mb-6">
        <a @click="mainModalIsOpen = false">Get Started for FREE</a>
      </div>
      <v-form ref="form" lazy-validation style="width:100%">
        <v-text-field
          outlined
          autofocus
          hide-details
          label="Email"
          v-model="email"
          :rules="[rules.email]"
        ></v-text-field>
        <v-text-field
          outlined
          hide-details
          label="Password"
          class="mt-3 mb-1"
          v-model="password"
          :rules="[rules.required]"
          :append-icon="showPW ? 'mdi-eye-off' : 'mdi-eye'"
          :type="showPW ? 'text' : 'password'"
          @click:append="showPW = !showPW"
          @keyup.enter="login"
          autocomplete="new-password"
        ></v-text-field>
        <!-- forget pasword button & dialog  -->
        <v-dialog v-model="loginDialog" max-width="600">
          <template v-slot:activator="{ on }">
            <p v-on="on" class="forgotPasswordLink smallText ma-0 mb-2 text-left"
              >I lost my password</p
            >
          </template>
          <v-card class="pa-5">
            <div id="closeIcon">
              <v-btn right icon @click.native="closePasswordResetModal">
                <v-icon color="teal">mdi-close</v-icon>
              </v-btn>
            </div>
            <v-card-title class="headline modal-headline">
              Lost password
            </v-card-title>

            <transition name="fade">
              <div v-if="!sending && !resetEmailSuccess && loginDialog" key="unsent">
                <v-card-text>
                  Please enter your email address below and we'll send you a recovery email.
                </v-card-text>
                <v-text-field
                  autofocus
                  ref="forgotPasswordField"
                  outlined
                  label="Email"
                  v-model="resetEmail"
                  @keyup.enter="sendPasswordResetEmail"
                ></v-text-field>
                <span class="red--text">{{ errorText }}</span>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    :loading="sending"
                    dark
                    color="blue darken-2"
                    v-if="!sending && !resetEmailSuccess"
                    @click.native="sendPasswordResetEmail"
                    >Recover Password</v-btn
                  >
                </v-card-actions>
              </div>
            </transition>
            <transition name="fade">
              <v-progress-circular
                key="sending"
                v-if="sending && !resetEmailSuccess"
                indeterminate
                color="teal"
              ></v-progress-circular>
            </transition>
            <transition name="fade">
              <v-card-text v-if="!sending && resetEmailSuccess" key="success">
                <v-icon left color="green">mdi-check-circle-outline</v-icon>
                Please follow the instructions in the email we've sent you to reset your password.
              </v-card-text>
            </transition>
          </v-card>
        </v-dialog>
        <v-btn
          class="white--text"
          color="blue darken-2 mb-1"
          large
          width="200"
          :loading="isLoggingIn"
          v-on:click="login"
        >
          Sign In
        </v-btn>
      </v-form>

      <div class="mt-1"><b>OR</b></div>
      <v-btn
        class="white--text mt-3"
        color="blue-grey darken-1"
        small
        v-on:click="loginWithGoogle"
        :loading="isLoggingInWithGoogle"
        style="font-weight:5000"
      >
        <svg class="authSVG" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path
            stroke="white"
            fill="white"
            d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"
          />
        </svg>
        Continue with google
      </v-btn>
    </v-card>
    <!-- invite confirmation modal -->
    <!-- shown when user has an invite -->
    <v-dialog v-model="isInviteConfirmationModalOpen" :max-width="'800px'" persistent attach>
      <v-card class="pa-6">
        <v-card-title class="display-1 justify-center">Invite Confirmation</v-card-title>
        <v-card-text class="text-md-center">
          You're attempting to login with an email address that has a pending invitation.<br />
          Please use the invite link in your inbox to accept or cancel this invitation, or click
          'Resend' to resend the invitation. Click 'Cancel' if you wish to login with a different
          email.</v-card-text
        >
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="white--text"
            :loading="isInviteResending"
            color="blue darken-2"
            @click="handleResendInviteClick"
          >
            Resend
          </v-btn>
          <v-btn dark class="ml-4" color="grey lighten-1" @click="handleCancelInviteClick">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- invite resent modal -->
    <v-dialog v-model="isInviteResentModalOpen" :max-width="'800px'" persistent attach>
      <v-card class="pa-6">
        <v-card-title class="display-1 justify-center">Invite resent successfully!</v-card-title>
        <v-card-text class="text-md-center">
          Please continue to accept the invite through your email.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn dark class="ml-4" color="grey lighten-1" @click="isInviteResentModalOpen = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- invite cannot be resent modal -->
    <v-dialog v-model="isInviteNotResentModalOpen" :max-width="'800px'" persistent attach>
      <v-card class="pa-6">
        <v-card-title class="display-1 justify-center">Invite could not be resent</v-card-title>
        <v-card-text class="text-md-center">
          The invite could not be resent. Please try again later or contact the person who invited
          you for a new invite.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            dark
            class="ml-4"
            color="grey lighten-1"
            @click="
              guibus_publish('dispatch:beacon', {
                eventId: `welcome-login_modal_close_button-click${isMobileSized ? '-mobile' : ''}`,
              })
              isInviteNotResentModalOpen = false
            "
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import firebaseService from 'firebase-service'
import AccountPreloginInterfaceFactory from 'bs-interface-account/bs-interface-account-prelogin.js'
import billingAccountInterface from 'bs-interface-billing-account'
import advertiserFactory from 'bs-interface-advertiser'
import CampaignInterfaceFactory from 'bs-interface-campaign'

export default {
  name: 'login',
  props: {
    loginModalIsOpen: {
      type: Boolean,
      default: false,
    },
    isMobileSized: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      valid: true,
      showPW: false,
      email: '',
      errorText: '',
      password: '',
      resetEmail: '',
      resetEmailSuccess: false,
      loginDialog: false,
      mainModalIsOpen: false,
      sending: false,
      rules: {
        required: value => !!value || 'Required.',
        email: () => {
          if (this.vemail) {
            return true
          } else {
            return 'Invalid e-mail.'
          }
        },
      },
      isLoggingIn: false,
      isLoggingInWithGoogle: false,
      // states of invite user
      isInviteConfirmationModalOpen: false,
      isInviteResentModalOpen: false,
      isInviteNotResentModalOpen: false,
      isInviteResending: false,
      isInviteChecked: false,
      isUserInvited: false,
    }
  },
  watch: {
    loginModalIsOpen: {
      handler(isOpen) {
        if (isOpen) {
          this.mainModalIsOpen = true
        }
      },
    },
    mainModalIsOpen: {
      handler(isOpen) {
        if (!isOpen) {
          this.$emit('modalClosed', false)
        }
      },
    },
  },
  methods: {
    login() {
      if (this.$refs.form.validate()) {
        // strip whitespace from email
        this.email = this.email.trim()
        this.isLoggingIn = true
        firebaseService
          .auth()
          .signInWithEmailAndPassword(this.email, this.password)
          .then(() => this.oAuthLoginhandler())
          .catch(err => {
            let errMsg = ''
            switch (err.code) {
              case 'auth/user-not-found':
                errMsg =
                  'An account could not be found with the given email address. Please try a different email or sign up for a new account.'
                break
              case 'auth/wrong-password':
                errMsg = 'Incorrect password entered. Please try again.'
                break
              default:
                errMsg = 'An error occurred while trying to login. Please try again.'
                break
            }
            this.isLoggingIn = false
            this.handleFailedLogin(errMsg)
          })
      }
    },
    loginWithGoogle() {
      this.isLoggingInWithGoogle = true
      var provider = new firebaseService.auth.GoogleAuthProvider()
      firebaseService
        .auth()
        .signInWithPopup(provider)
        .then(r => {
          this.email = r.user.email
          this.oAuthLoginhandler()
        }) // handles successful login, with main.js redirecting user to home
        .catch(err => {
          // closing the modal without logging in is considered an error, so only display an error if there is a real one
          if (err.code != 'auth/popup-closed-by-user') {
            this.handleFailedLogin('An error occurred while trying to login. Please try again.')
          }
          this.isLoggingInWithGoogle = false
        })
    },
    async oAuthLoginhandler() {
      const firebaseUser = firebaseService.auth().currentUser
      // Check if user has invite
      await this.AccountPreloginInterface.isInvited(btoa(firebaseUser.email)).then(response => {
        // expect response value: true/false as string
        this.isUserInvited = response === 'true'
        this.isInviteChecked = true
      })
      if (this.isUserInvited) {
        firebaseUser.delete().catch(err => {
          console.error(err)
        })
        this.isInviteConfirmationModalOpen = true
        this.isLoggingIn = false
        this.isLoggingInWithGoogle = false
        return
      }
      window.location.reload()
    },
    async handleResendInviteClick() {
      this.isInviteResending = true

      // check if invite can be resent
      await this.AccountPreloginInterface.resendInviteByUser(btoa(this.email)).then(response => {
        // expect response value: true/false as string
        const canBeResent = response === 'true'

        this.isInviteConfirmationModalOpen = false
        if (canBeResent) {
          this.isInviteResentModalOpen = true
        } else {
          this.isInviteNotResentModalOpen = true
        }
      })
      this.isInviteResending = false
      this.handleCancelInviteClick()
    },
    handleCancelInviteClick() {
      this.isInviteConfirmationModalOpen = false
      this.isInviteChecked = false
      this.isUserInvited = false
      this.email = ''
    },
    processUserLogin() {
      // to prevent users with disabled billing account from logging in,
      // first check if the billing account is disabled
      // if they are not disabled, then determine to redirect them to campaign-creative or campaign-manager
      // else, the application will log them out and display an error
      billingAccountInterface(this.$firebaseUser)
        .getIsDisabled(async isDisabled => {
          if (!isDisabled) {
            let advertisersList = undefined
            // redirect user based on number of advertiser/campaigns
            // If someone has zero or more than one advertiser, drop them on the advertiser list page.
            // If someone has one advertiser and zero or more than one campaign, drop them off on the campaign manager page.
            // If someone has one advertiser and one campaign, drop them off on the creative editor page.
            await new Promise(resolve => {
              advertiserFactory().getAll(advertisers => {
                if (advertisers.length === 0 || advertisers.length > 1) {
                  window.location = '/campaign-manager/v1/page'
                } else {
                  advertisersList = advertisers
                  resolve()
                }
              })
            })
            await CampaignInterfaceFactory().getList(advertisersList[0].AdvertiserID, campaigns => {
              if (campaigns.length === 0 || campaigns.length > 1) {
                window.location = '/campaign-manager/v1/page'
              } else {
                window.location = `/campaign-creative/v2/page/ai/${advertisersList[0].AdvertiserID}/ci/${campaigns[0].CampaignID}`
              }
            })
          } else {
            firebaseService.auth().signOut()
            localStorage.setItem(
              'adaSystemMsg',
              '{"isShownImmediately":true,"type":"error","message":"Your billing account has been disabled. Please contact support if you wish to resolve this issue."}'
            )
            location.reload()
          }
        })
        .catch(() => {
          // TODO - if the accountbillingaccount service is down for whatever reason at this moment, a user
          // with a disabled billing account will be able to log in, the BSCS needs to be used here to determine
          // the exact error code/message to handle the difference between the service being down, or an account not
          // being found (ie. during sign up)
          window.location = '/campaign-manager/v1/page'
        })
    },
    handleFailedLogin(errMsg) {
      this.$emit('setSystemMessage', {
        isShownImmediately: true,
        type: 'error',
        message: errMsg,
      })
    },
    // password reset methods
    sendPasswordResetEmail() {
      // strip whitespace from email
      this.resetEmail = this.resetEmail.trim()
      this.sending = true
      this.errorText = ''
      firebaseService
        .auth()
        .sendPasswordResetEmail(this.resetEmail)
        .then(this.notifyUserOfSentEmail)
        .catch(this.notifyUserOfError)
    },
    notifyUserOfSentEmail() {
      this.sending = false
      this.resetEmailSuccess = true
      this.resetEmail = ''
    },
    notifyUserOfError() {
      this.sending = false
      this.errorText = 'There is no account associated with that email address.'
    },
    closePasswordResetModal() {
      this.loginDialog = false
      this.sending = false
      this.resetEmailSuccess = false
      if (this.$refs.forgotPasswordField) {
        this.$refs.forgotPasswordField.blur()
      }
    },
  },
  computed: {
    vemail() {
      const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return pattern.test(this.email)
    },
    AccountPreloginInterface() {
      return AccountPreloginInterfaceFactory()
    },
    AccountInterface() {
      return window.adacado.bscs ? window.adacado.bscs.AccountInterfaceFactory() : undefined
    },
  },
}
</script>

<style lang="scss" scoped>
.loginMainWrapper {
  background: rgba(94, 0, 235, 0.24);
}

.panel {
  text-align: center;
  * {
    transition: all 1.5s cubic-bezier(0.25, 0.46, 0, 0.99);
  }
  &.regularPanel {
    width: 400px;
    height: 508px;
    min-width: 400px;
  }
}

.fadeInSlideUp-enter-active,
.fadeInSlideUp-leave-active {
  transition: all 1s cubic-bezier(0.25, 0.46, 0, 0.99);
  transform: translateY(0%);
}
.fadeInSlideUp-enter,
.fadeInSlideUp-leave-to {
  transform: translateY(10%);
  opacity: 0;
}

.authSVG {
  width: 14px;
  height: 14px;
  margin-right: 15px;
}

#closeIcon {
  display: flex;
  justify-content: flex-end;
}

.modal-headline {
  justify-content: center;
}

.smallText {
  font-size: 14px;
}

.v-btn {
  letter-spacing: 0;
}

.forgotPasswordLink {
  cursor: pointer;
  color: #1976d2;
  text-decoration: underline;
}
</style>
